import { Fragment, useMemo, useState } from 'react';

import Settings from '@crehana/icons/Settings';
import { nextSeoDefaultProps, type TAppProps } from '@crehana/next';
import {
  PrivatePage,
  useMe,
  useSavedCoursesContext,
  useUITheme,
} from '@crehana/web';
import { UserDegreeSegmentationEnum } from '@crehana/web/__generated__/graphql-schema';
import Head from 'next/head';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { BrowserRouter } from 'react-router-dom';

import {
  DashboardMenu,
  DashboardRoot,
  DesktopFAQs,
  MobileFAQs,
  Onboarding,
  SavedCoursesOnboardingTour,
  SearchDialog,
} from './components';
import { useCrehanaTranslation, useGetStudentType } from './hooks';
import routes, {
  BASE_URL,
  ENROLLMENTS_DEGREES_ROUTE,
  ENROLLMENTS_ROUTE,
  SAVED_COURSES_KEY,
} from './routes/config';
import RouteSwitch from './routes/RouteSwitch';
import SEO, { getPageTitleFromUrl } from './SEO';
import type { IAppProps, UserDashboardLocaleKeys } from './types';

const App: React.FC<TAppProps<IAppProps>> = ({
  languageCode,
  countryCode,
  pageUrl,
}) => {
  const t = useCrehanaTranslation();
  const { isDark } = useUITheme();
  const { me } = useMe();
  const hasActiveDegree =
    me?.userDegreeSegmentation ===
    UserDegreeSegmentationEnum.ACTIVATED_DEGREE_SEGMENTATION;
  const enrollmentsPath = hasActiveDegree
    ? `${ENROLLMENTS_ROUTE.path}${ENROLLMENTS_DEGREES_ROUTE.path}`
    : ENROLLMENTS_ROUTE.path;
  const [tourIsOpen, setTourIsOpen] = useState<boolean>(false);

  const [shouldValidateSavedCoursesTour, setShouldValidateSavedCoursesTour] =
    useState(false);
  const [searchDialogIsOpen, setSearchDialogIsOpen] = useState<boolean>(false);
  const [mobileFAQsIsOpen, setMobileFAQsIsOpen] = useState<boolean>(false);
  const { isEnabledSaveCourses } = useSavedCoursesContext();
  const studentType = useGetStudentType();

  const availableRoutes = useMemo(() => {
    if (isEnabledSaveCourses) {
      return routes;
    }

    // Show all routes except Saved Courses if the user do not have a membership
    return routes.filter(route => route.key !== SAVED_COURSES_KEY);
  }, [isEnabledSaveCourses]);

  const toggleTour = () => setTourIsOpen(!tourIsOpen);

  const toggleSearchDialog = () => {
    setSearchDialogIsOpen(!searchDialogIsOpen);
  };

  const toggleMobileFAQ = () => {
    setMobileFAQsIsOpen(!mobileFAQsIsOpen);
  };

  return (
    <Fragment>
      {/* Default Seo Tags, the real dynamic tags live inside the SEO.tsx file */}
      <Head>
        <title>{t(getPageTitleFromUrl(pageUrl))} | Crehana</title>
        <meta name="description" content={nextSeoDefaultProps.description} />
        <meta
          property="og:image"
          content="https://static.crehana.com/static/img/logo/logo-crehana.6a48047513e0.png"
        />
      </Head>
      <PrivatePage isDark languageCode={languageCode}>
        <BrowserRouter basename={BASE_URL}>
          <DashboardRoot supportsDarkMode>
            <DashboardMenu
              routes={availableRoutes.map(r => ({
                ...r,
                path: r.key === 'ENROLLMENTS' ? enrollmentsPath : r.path,
                label: t(`UD_${r.key}_TITLE` as UserDashboardLocaleKeys),
              }))}
              supportsDarkMode
            >
              <SEO />
              <div className="flex flex-col pt-16">
                <a
                  className="flex items-center font-label text-base-lighter py-8 px-20"
                  href="/configuracion/"
                  data-tour="sidebar-config"
                >
                  <Settings
                    size={24}
                    ignoreColor
                    className="mr-8 fill-current"
                  />
                  {t('UD_CONFIGURATION')}
                </a>
                <DesktopFAQs
                  toggleTour={toggleTour}
                  studentType={studentType}
                />
              </div>
            </DashboardMenu>
            <RouteSwitch
              countryPrefix={countryCode}
              onSearchClick={toggleSearchDialog}
              onMobileFAQsClick={toggleMobileFAQ}
              isDark={isDark}
            />
            <SearchDialog
              open={searchDialogIsOpen}
              onDismiss={toggleSearchDialog}
            />
            <Onboarding
              tourIsOpen={tourIsOpen}
              toggleTour={toggleTour}
              validateSavedCoursesTour={() => {
                setShouldValidateSavedCoursesTour(true);
              }}
            />
            {shouldValidateSavedCoursesTour && <SavedCoursesOnboardingTour />}
            <MobileFAQs
              open={mobileFAQsIsOpen}
              onDismiss={toggleMobileFAQ}
              toggleTour={toggleTour}
              studentType={studentType}
            />
          </DashboardRoot>
        </BrowserRouter>
      </PrivatePage>
    </Fragment>
  );
};

export default App;
